import React, { useState, useEffect } from 'react'
import { FileText, CheckSquare, Mic } from 'lucide-react'
import { Loader2 } from 'lucide-react'
import { fetchWithAuth } from '@/lib/api'
import { withWorkspaceScope } from '@/lib/workspace-scope-query'
import { WorkspaceChipRow } from '@/components/features/workspace-chip-row'

interface Stats {
  documentsEditedToday?: number
  todosCompletedToday?: number
  voiceMemosTranscribedToday?: number
}

interface WelcomeHeaderProps {
  activeWorkspaceIds?: string[]
}

export function WelcomeHeader({ activeWorkspaceIds = [] }: WelcomeHeaderProps) {
  const [stats, setStats] = useState<Stats>({})
  const [loading, setLoading] = useState(true)

  // Get user name from localStorage or use default
  const userName = typeof window !== 'undefined'
    ? localStorage.getItem('userName') || 'there'
    : 'there'

  useEffect(() => {
    const loadStats = async () => {
      try {
        // TODO: Replace with actual API endpoint when available
        // For now, use heuristics
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        // Count documents whose updated_at falls on local calendar today
        try {
          const docsResponse = await fetchWithAuth(
            withWorkspaceScope('/api/documents/recent?limit=100&sortBy=edited', activeWorkspaceIds)
          )
          if (docsResponse.ok) {
            const docsData = await docsResponse.json()
            const startOfToday = today.getTime()
            const editedToday = (docsData.documents || []).filter((doc: { updatedAtRaw?: string }) => {
              if (!doc.updatedAtRaw) return false
              const t = new Date(doc.updatedAtRaw).getTime()
              return Number.isFinite(t) && t >= startOfToday
            }).length
            setStats(prev => ({ ...prev, documentsEditedToday: editedToday }))
          }
        } catch (err) {
          console.error('[WelcomeHeader] Error loading stats:', err)
        }
      } catch (err) {
        console.error('[WelcomeHeader] Error loading stats:', err)
      } finally {
        setLoading(false)
      }
    }

    loadStats()
  }, [activeWorkspaceIds])

  const hasStats = stats.documentsEditedToday !== undefined || 
                   stats.todosCompletedToday !== undefined || 
                   stats.voiceMemosTranscribedToday !== undefined

  const isNewUser = !hasStats || (
    (stats.documentsEditedToday === undefined || stats.documentsEditedToday === 0) &&
    (stats.todosCompletedToday === undefined || stats.todosCompletedToday === 0) &&
    (stats.voiceMemosTranscribedToday === undefined || stats.voiceMemosTranscribedToday === 0)
  )
  const greeting = isNewUser ? 'Welcome' : 'Welcome back'

  return (
    <div className="border-b border-border/40 pb-5 mb-1">
      <h1 className="text-2xl font-semibold tracking-tight mb-1.5 text-foreground">
        {greeting}{userName !== 'there' ? `, ${userName}` : ''}
      </h1>
      <p className="text-sm text-muted-foreground mb-4">Your workspace at a glance</p>
      
      {hasStats && (
        <div className="flex items-center gap-6 text-sm text-muted-foreground">
          {stats.documentsEditedToday !== undefined && (
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span>{stats.documentsEditedToday} document{stats.documentsEditedToday !== 1 ? 's' : ''} edited today</span>
            </div>
          )}
          {stats.todosCompletedToday !== undefined && stats.todosCompletedToday > 0 && (
            <div className="flex items-center gap-2">
              <CheckSquare className="h-4 w-4" />
              <span>{stats.todosCompletedToday} todo{stats.todosCompletedToday !== 1 ? 's' : ''} completed</span>
            </div>
          )}
          {stats.voiceMemosTranscribedToday !== undefined && stats.voiceMemosTranscribedToday > 0 && (
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4" />
              <span>{stats.voiceMemosTranscribedToday} voice memo{stats.voiceMemosTranscribedToday !== 1 ? 's' : ''} transcribed</span>
            </div>
          )}
        </div>
      )}

      <div className="mt-5">
        <WorkspaceChipRow />
      </div>
    </div>
  )
}

